import React, { useEffect, useState } from "react";
import { DateAnswerFormat, RelativeDateConstraint } from "../../../../types/Survey";
import "./DateQuestion.css"
import { formatISO, isValid, parse, parseISO } from "date-fns";
import SurveyInput from "../../Components/SurveyInput/SurveyInput";
import useTranslate from "../../../../hooks/useTranslate";
import LocalizedText from "../../../presentational/LocalizedText";
import getDateFromConstraint from "../../Util/dateHelpers";
import { SurveyQuestionProps } from "../../Components/SurveyQuestion/SurveyQuestion";
import SurveyFormItem from "../../Components/SurveyFormItem/SurveyFormItem";
import { QuestionResultModel } from "../../../../types/SurveyResult";
import MaskedDateInput from "./MaskedDateInput";

export interface SurveyQuestionsLocaleOverride extends SurveyQuestionProps {
    locale?: string;
}

export default function (props: SurveyQuestionsLocaleOverride) {
    let [validationError, setValidationError] = useState<string | undefined>(undefined);
    let translations = useTranslate(["default-date-placeholder", "default-date-time-placeholder"]);
    let currentResult = props.result as QuestionResultModel;
    const currentAnswer = currentResult.Answers[0] || "";

    let answerFormat = props.answerFormat as DateAnswerFormat;
    let showMaskedTextEntry = answerFormat.style === "Date" &&
        answerFormat.textEntry &&
        props.variant === "v2" ?
        true : false;

    let resultChanged = (fullDate: string) => {
        if (answerFormat.style == "DateAndTime" && fullDate) {
            let parsedDate = parseISO(fullDate);
            fullDate = formatISO(parsedDate);
        }

        if (getValidationError(fullDate)) {
            props.onResultChange({ ...props.result, Answers: [fullDate], invalid: true } as QuestionResultModel);
            return;
        }
        setValidationError(undefined);
        props.onResultChange({ ...props.result, Answers: [fullDate], invalid: false } as QuestionResultModel);
    }

    function getValidationError(answer: string): 'date-validation-error' | 'date-validation-error-min' | 'date-validation-error-max' | undefined {
        if (answer == "" && props.optional) {
            return;
        }

        const parsedDate = parseISO(answer.substring(0, 16));
        if (!isValid(parsedDate)) {
            return 'date-validation-error';
        }

        let minimumDate = getDateFromConstraint(answerFormat.minimumDate);
        if (minimumDate && parsedDate < minimumDate) {
            return 'date-validation-error-min';
        }

        let maximumDate = getDateFromConstraint(answerFormat.maximumDate);
        if (maximumDate && parsedDate > maximumDate) {
            return 'date-validation-error-max';
        }
    }

    function localeFormatDateConstraint(dateConstraint: string | RelativeDateConstraint | undefined): string | undefined {
        let date = getDateFromConstraint(dateConstraint);
        if (!date) return;
        if (answerFormat.style == "DateAndTime") {
            return date.toLocaleString();
        }
        return date.toLocaleDateString();
    }

    let placeholder = props.placeholder || (answerFormat.style == "DateAndTime" ? translations["default-date-time-placeholder"] : translations["default-date-placeholder"]);

    function ISOFormatDateConstraint(dateConstraint: string | RelativeDateConstraint | undefined): string | undefined {
        let date = getDateFromConstraint(dateConstraint);
        if (!date) { return; }
        if (answerFormat.style == "DateAndTime") {
            return formatISO(date).substring(0, 16);
        }
        return formatISO(date).substring(0, 10);
    }

    function setDefaultDate() {
        resultChanged(ISOFormatDateConstraint(answerFormat.defaultDate) || "");
        setValidationError(getValidationError(ISOFormatDateConstraint(answerFormat.defaultDate) || ""));
    }

    useEffect(() => {
        // set initial validation error if the answer is not valid
        if (currentAnswer) {
            resultChanged(currentAnswer);
            setValidationError(getValidationError(currentAnswer))
        }
    }, []);

    return <SurveyFormItem className="date-question"
        bottomBorder
        topBorder
        label={props.label}
        labelFor={props.inputIdForLabel}
        index={props.index}
        labelPosition={props.labelPosition}
        useFieldsetFormat={props.useFieldsetFormat}
        validationError={validationError ? <LocalizedText textKey={validationError} values={{
            minimumDate: localeFormatDateConstraint(answerFormat.minimumDate),
            maximumDate: localeFormatDateConstraint(answerFormat.maximumDate)
        }} /> : undefined}
        variant={props.variant}>
        {showMaskedTextEntry &&
            <MaskedDateInput currentValue={currentAnswer}
                id={props.inputIdForLabel}
                onBlur={(currValue) => setValidationError(getValidationError(currValue!))}
                onResultChange={resultChanged}
                placeholder={props.placeholder} locale={props.locale} />
        }
        {!showMaskedTextEntry && <SurveyInput type={answerFormat.style == "DateAndTime" ? "datetime-local" : "date"}
            id={props.inputIdForLabel}
            aria-describedby={validationError ? `${props.inputIdForLabel + "-error"}` : ''}
            value={answerFormat.style == "DateAndTime" ? currentAnswer.substring(0, 16) : currentAnswer}
            placeholder={placeholder}
            className="date-picker"
            onChange={(d) => resultChanged(d.target.value)}
            onBlur={() => setValidationError(getValidationError(currentAnswer))}
            onEnterPressed={props.onEnterPressed}
            min={ISOFormatDateConstraint(answerFormat.minimumDate)}
            max={ISOFormatDateConstraint(answerFormat.maximumDate)}
            onPlaceholderButtonClick={() => setDefaultDate()}
            autoComplete={props.autoComplete}
            variant={props.variant} />
        }
    </SurveyFormItem>
}